import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../api/axios';
import { ArrowLeft, Play, Plus, Trash2, Upload, Users, CheckCircle, BookOpen, ClipboardList, X } from 'lucide-react';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
};

export default function AdminCourseDetail() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmModal, setConfirmModal] = React.useState({ open: false, action: null, title: '', message: '' });
  const showConfirm = (title, message, action) => setConfirmModal({ open: true, title, message, action });
  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: '', message: '' });
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const fileRef = useRef();
  const thumbRef = useRef();
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [ratings, setRatings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [completing, setCompleting] = useState(false);
  const [completeSuccess, setCompleteSuccess] = useState('');
  const [course, setCourse] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [videoForm, setVideoForm] = useState({ title: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [quiz, setQuiz] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizForm, setQuizForm] = useState({ title: 'Eğitim Sonu Sınavı', description: '', passing_score: 70, questions: [] });
  const [savingQuiz, setSavingQuiz] = useState(false);

  const load = () => api.get('/courses/' + id).then(r => { setCourse(r.data); setThumbnail(r.data.thumbnail); });
  const loadQuiz = () => api.get('/quizzes/admin/course/' + id).then(r => setQuiz(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get('/users').then(r => setAllUsers(r.data.filter(u => u.role === 'student' || u.role === 'manager')));
    api.get('/courses/' + id + '/ratings').then(r => setRatings(r.data)).catch(() => {});
    api.get('/quizzes/admin/course/' + id).then(r => setQuiz(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    if (course) api.get('/reports/course/' + id).then(r => setEnrolledUsers(r.data));
  }, [course]);

  const uploadThumbnail = async (file) => {
    if (!file) return;
    setThumbUploading(true);
    const formData = new FormData();
    formData.append('thumbnail', file);
    try {
      await api.post('/thumbnails/course/' + id, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch(e) { alert('Resim yuklenemedi'); }
    setThumbUploading(false);
  };

  const completeUser = async () => {
    if (!selectedUser) return;
    setCompleting(true);
    try {
      await api.post('/courses/' + id + '/complete-user', { user_id: selectedUser });
      const u = allUsers.find(x => x.id === selectedUser);
      setCompleteSuccess((u?.name || 'Kullanıcı') + ' için eğitim tamamlandı olarak işaretlendi!');
      setSelectedUser('');
      setTimeout(() => { setCompleteSuccess(''); setCompleteModal(false); }, 2000);
    } catch(e) { alert(e.response?.data?.error || 'Hata oluştu'); }
    setCompleting(false);
  };

  const deleteThumbnail = async () => {
  // modal ile
    await api.delete('/thumbnails/course/' + id);
    setThumbnail(null); load();
  };

  const saveQuiz = async () => {
    if (!quizForm.title.trim()) return;
    if (quizForm.questions.length === 0) { alert('En az 1 soru ekleyin'); return; }
    setSavingQuiz(true);
    try {
      if (quiz) {
        await api.put('/quizzes/' + quiz.id, { ...quizForm });
      } else {
        await api.post('/quizzes', { ...quizForm, course_id: id });
      }
      loadQuiz();
      setShowQuizModal(false);
    } catch(e) { alert(e.response?.data?.error || 'Hata oluştu'); }
    setSavingQuiz(false);
  };

  const deleteQuiz = async () => {
    if (!quiz) return;
    await api.delete('/quizzes/' + quiz.id);
    setQuiz(null);
  };

  const addQuestion = () => {
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, { question: '', options: ['', '', '', ''], correct_answer: 0 }]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setQuizForm(prev => {
      const qs = [...prev.questions];
      qs[index] = { ...qs[index], [field]: value };
      return { ...prev, questions: qs };
    });
  };

  const updateOption = (qIndex, oIndex, value) => {
    setQuizForm(prev => {
      const qs = [...prev.questions];
      const opts = [...qs[qIndex].options];
      opts[oIndex] = value;
      qs[qIndex] = { ...qs[qIndex], options: opts };
      return { ...prev, questions: qs };
    });
  };

  const removeQuestion = (index) => {
    setQuizForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== index) }));
  };

  const openQuizModal = () => {
    if (quiz) {
      setQuizForm({
        title: quiz.title,
        description: quiz.description || '',
        passing_score: quiz.passing_score,
        questions: quiz.questions.map(q => ({
          question: q.question,
          options: q.options,
          correct_answer: q.correct_answer
        }))
      });
    } else {
      setQuizForm({ title: 'Eğitim Sonu Sınavı', description: '', passing_score: 70, questions: [] });
    }
    setShowQuizModal(true);
  };

  const addSection = async () => {
    if (!sectionTitle.trim()) return;
    await api.post('/sections', { course_id: id, title: sectionTitle, order_index: course?.sections?.length || 0 });
    setSectionTitle(''); setShowSectionModal(false); load();
  };

  const deleteVideo = async (videoId) => {
  // modal ile
    await api.delete('/videos/' + videoId); load();
  };

  const deleteSection = async (sectionId) => {
  // modal ile
    await api.delete('/sections/' + sectionId); load();
  };

  const uploadVideo = async () => {
    if (!videoForm.file || !videoForm.title || !activeSection) return;
    setUploading(true); setUploadProgress(0);
    const formData = new FormData();
    formData.append('video', videoForm.file);
    formData.append('section_id', activeSection.id);
    formData.append('title', videoForm.title);
    formData.append('order_index', activeSection.videos?.length || 0);
    try {
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => setUploadProgress(Math.round(e.loaded * 100 / e.total))
      });
      setVideoForm({ title: '', file: null }); setShowVideoModal(false); setUploading(false); load();
    } catch (err) { alert('Video yüklenemedi: ' + err.message); setUploading(false); }
  };

  const enrollUser = async () => {
    if (!selectedUser) return;
    await api.post('/courses/' + id + '/enroll-user', { user_id: selectedUser });
    setSelectedUser(''); setShowEnrollModal(false);
    api.get('/reports/course/' + id).then(r => setEnrolledUsers(r.data));
  };

  if (!course) return <AdminLayout><div style={{ padding: 40 }}>Yükleniyor...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate('/yonetim/egitimler')} style={{ background: 'rgba(0,122,255,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#007aff', fontWeight: 600, fontSize: 13, padding: '8px 14px', borderRadius: 10 }}>
          <ArrowLeft size={15} /> Geri
        </button>
        <h2 style={{ fontWeight: 700, fontSize: isMobile ? 16 : 20, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.title}</h2>
        <button onClick={() => setCompleteModal(true)}
          style={{ background: 'linear-gradient(135deg, #34c759, #30b350)', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          Eğitim Kullanıcıları
        </button>
      </div>

      {/* Kapak Resmi */}
      <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 14, padding: '16px 18px', marginBottom: 16, border: '1px solid rgba(255,255,255,0.9)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20, flexWrap: 'wrap' }}>
        <div style={{ width: isMobile ? 120 : 160, height: isMobile ? 68 : 90, borderRadius: 10, overflow: 'hidden', background: 'linear-gradient(135deg, #007aff, #5856d6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {thumbnail
            ? <img src={'/uploads/thumbnails/' + thumbnail + '?v=' + Date.now()} alt="Kapak" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <BookOpen size={28} color="rgba(255,255,255,0.7)" strokeWidth={1.5} />}
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', marginBottom: 4 }}>Kapak Resmi</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>800x450px önerilen. JPG, PNG, WebP</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input ref={thumbRef} type="file" accept="image/*" onChange={e => uploadThumbnail(e.target.files[0])} style={{ display: 'none' }} />
            <button onClick={() => thumbRef.current.click()} disabled={thumbUploading}
              style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              {thumbUploading ? 'Yükleniyor...' : thumbnail ? 'Değiştir' : 'Resim Yükle'}
            </button>
            {thumbnail && isAdmin && <button onClick={deleteThumbnail} style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Sil</button>}
          </div>
        </div>
      </div>

      {/* Ana içerik - mobilde alt alta, masaüstünde yan yana */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 300px', gap: 16 }}>

        {/* Sol: İçerik yapısı */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>İçerik Yapısı</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={openQuizModal}
                style={{ background: quiz ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #34c759, #30b350)', color: 'white', border: 'none', borderRadius: 10, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <ClipboardList size={13} /> {quiz ? 'Sınavı Düzenle' : 'Sınav Ekle'}
              </button>
              {quiz && (
                <button onClick={deleteQuiz}
                  style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: 10, padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                  <Trash2 size={13} />
                </button>
              )}
              <button onClick={() => setShowSectionModal(true)} style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Plus size={13} /> Bölüm Ekle
              </button>
            </div>
          </div>

          {course.sections?.length === 0 && (
            <div style={{ background: 'rgba(255,255,255,0.8)', borderRadius: 12, padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henüz bölüm yok.</div>
          )}

          {course.sections?.map(section => (
            <div key={section.id} style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 12, marginBottom: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.9)' }}>
              <div style={{ padding: '10px 14px', background: 'rgba(0,122,255,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.05)', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 7 }}>
                  📁 {section.title}
                  <span style={{ background: 'rgba(0,122,255,0.1)', color: '#007aff', borderRadius: 20, padding: '2px 7px', fontSize: 10, fontWeight: 600 }}>{section.videos?.length || 0}</span>
                </div>
                <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
                  <button onClick={() => { setActiveSection(section); setShowVideoModal(true); }}
                    style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Upload size={11} /> Video Ekle
                  </button>
                  {isAdmin && <button onClick={() => deleteSection(section.id)}
                    style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: 7, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Trash2 size={12} />
                  </button>}
                </div>
              </div>

              {(!section.videos || section.videos.length === 0) && (
                <div style={{ padding: '12px 14px', color: '#9ca3af', fontSize: 12 }}>Bu bölümde henüz video yok.</div>
              )}

              {section.videos?.map((video, idx) => (
                <div key={video.id} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #007aff, #5856d6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 10, flexShrink: 0 }}>{idx + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{video.title}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>⏱ {video.duration || 0} dk</div>
                  </div>
                  <span style={{ background: 'rgba(52,199,89,0.1)', color: '#34c759', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                    <CheckCircle size={9} /> OK
                  </span>
                  {isAdmin && <button onClick={() => deleteVideo(video.id)}
                    style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: 'none', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Trash2 size={12} />
                  </button>}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Sağ: Kayıtlı kullanıcılar */}
        <div>
          <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid rgba(255,255,255,0.9)' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={14} color="#007aff" /> Kayıtlı ({enrolledUsers.length})
              </div>
              <button onClick={() => setShowEnrollModal(true)}
                style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>+ Ekle</button>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {enrolledUsers.length === 0 && <div style={{ padding: 18, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>Henüz kayıtlı kullanıcı yok.</div>}
              {enrolledUsers.map((u, i) => (
                <div key={i} style={{ padding: '9px 14px', borderBottom: '1px solid rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 9 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{u.name?.charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af' }}>%{u.progress_percent || 0}</div>
                  </div>
                  <div style={{ height: 4, width: 40, background: 'rgba(0,0,0,0.06)', borderRadius: 4, flexShrink: 0 }}>
                    <div style={{ height: '100%', width: (u.progress_percent || 0) + '%', background: '#007aff', borderRadius: 4 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modallar */}
      {/* Sınav Bilgi Kartı */}
      {quiz && (
        <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(217,119,6,0.05))', border: '1.5px solid rgba(245,158,11,0.3)', borderRadius: 14, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📝</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{quiz.title}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{quiz.questions?.length || 0} soru • Geçer not: %{quiz.passing_score}</div>
          </div>
          <span style={{ background: quiz.is_active ? 'rgba(52,199,89,0.1)' : 'rgba(0,0,0,0.05)', color: quiz.is_active ? '#34c759' : '#9ca3af', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
            {quiz.is_active ? 'Aktif' : 'Pasif'}
          </span>
        </div>
      )}

      {/* Sınav Modal */}
      {showQuizModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '20px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 680, boxShadow: '0 32px 80px rgba(0,0,0,0.2)', marginBottom: 20 }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 800, fontSize: 18, color: '#1a1a2e' }}>📝 {quiz ? 'Sınavı Düzenle' : 'Sınav Oluştur'}</div>
              <button onClick={() => setShowQuizModal(false)} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {/* Sınav Bilgileri */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12, color: '#6b7280', textTransform: 'uppercase' }}>Sınav Başlığı</label>
                  <input value={quizForm.title} onChange={e => setQuizForm({...quizForm, title: e.target.value})}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12, color: '#6b7280', textTransform: 'uppercase' }}>Geçer Not (%)</label>
                  <input type="number" min="1" max="100" value={quizForm.passing_score} onChange={e => setQuizForm({...quizForm, passing_score: parseInt(e.target.value)})}
                    style={{ width: 100, padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', textAlign: 'center' }} />
                </div>
              </div>

              {/* Sorular */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>Sorular ({quizForm.questions.length})</label>
                  <button onClick={addQuestion} style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 9, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Plus size={13} /> Soru Ekle
                  </button>
                </div>

                {quizForm.questions.length === 0 && (
                  <div style={{ background: '#f9fafb', borderRadius: 12, padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Henüz soru yok. "Soru Ekle" butonuna tıklayın.
                  </div>
                )}

                {quizForm.questions.map((q, qIndex) => (
                  <div key={qIndex} style={{ background: '#f9fafb', borderRadius: 12, padding: '16px', marginBottom: 10, border: '1.5px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontWeight: 700, fontSize: 12, color: '#6b7280' }}>SORU {qIndex + 1}</span>
                      <button onClick={() => removeQuestion(qIndex)} style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: 'none', borderRadius: 7, padding: '3px 8px', cursor: 'pointer', fontSize: 11 }}>Sil</button>
                    </div>
                    <textarea value={q.question} onChange={e => updateQuestion(qIndex, 'question', e.target.value)}
                      placeholder="Soru metni..."
                      rows={2}
                      style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box', marginBottom: 10, fontFamily: 'inherit' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {q.options.map((opt, oIndex) => (
                        <div key={oIndex} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input type="radio" checked={q.correct_answer === oIndex} onChange={() => updateQuestion(qIndex, 'correct_answer', oIndex)}
                            style={{ accentColor: '#34c759', flexShrink: 0 }} title="Doğru cevap" />
                          <input value={opt} onChange={e => updateOption(qIndex, oIndex, e.target.value)}
                            placeholder={`${['A', 'B', 'C', 'D'][oIndex]} şıkkı`}
                            style={{ flex: 1, padding: '8px 10px', border: `1.5px solid ${q.correct_answer === oIndex ? '#34c759' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12, outline: 'none', background: q.correct_answer === oIndex ? 'rgba(52,199,89,0.05)' : 'white' }} />
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>💡 Doğru cevabın solundaki butona tıklayın</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                <button onClick={() => setShowQuizModal(false)}
                  style={{ flex: 1, padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: 50, background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#6b7280' }}>İptal</button>
                <button onClick={saveQuiz} disabled={savingQuiz}
                  style={{ flex: 1, padding: '12px', border: 'none', borderRadius: 50, background: 'linear-gradient(135deg, #34c759, #30b350)', color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                  {savingQuiz ? 'Kaydediliyor...' : '💾 Sınavı Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSectionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18, color: '#1a1a2e' }}>Yeni Bölüm</div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 13, color: '#6b7280' }}>BÖLÜM BAŞLIĞI</label>
            <input value={sectionTitle} onChange={e => setSectionTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSection()}
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', marginBottom: 18, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSectionModal(false)} style={{ padding: '9px 18px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white', cursor: 'pointer', fontWeight: 600 }}>İptal</button>
              <button onClick={addSection} style={{ padding: '9px 22px', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Ekle</button>
            </div>
          </div>
        </div>
      )}

      {showVideoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4, color: '#1a1a2e' }}>Video Yükle</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 18 }}>{activeSection?.title}</div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>VIDEO BAŞLIĞI</label>
              <input value={videoForm.title} onChange={e => setVideoForm({...videoForm, title: e.target.value})}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>VIDEO DOSYASI (H.264 MP4 önerilen)</label>
              <input ref={fileRef} type="file" accept=".mp4,.webm,.ts,.avi,.mkv,.m4v,.flv,video/*" onChange={e => setVideoForm({...videoForm, file: e.target.files[0]})}
                style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, boxSizing: 'border-box' }} />
              {videoForm.file && <div style={{ marginTop: 6, fontSize: 11, color: '#6b7280', background: '#f9fafb', padding: '5px 10px', borderRadius: 7 }}>📎 {videoForm.file.name} ({(videoForm.file.size/1024/1024).toFixed(1)} MB)</div>}
            </div>
            {uploading && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12, fontWeight: 600 }}>
                  <span style={{ color: '#007aff' }}>Yükleniyor...</span><span>%{uploadProgress}</span>
                </div>
                <div style={{ height: 6, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: uploadProgress + '%', background: 'linear-gradient(90deg, #007aff, #5856d6)', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowVideoModal(false); setVideoForm({ title: '', file: null }); }} disabled={uploading}
                style={{ padding: '9px 18px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white', cursor: 'pointer', fontWeight: 600 }}>İptal</button>
              <button onClick={uploadVideo} disabled={uploading || !videoForm.file || !videoForm.title}
                style={{ padding: '9px 22px', background: uploading ? '#9ca3af' : 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, cursor: uploading ? 'not-allowed' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Upload size={14} /> {uploading ? '%' + uploadProgress : 'Yükle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEnrollModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 18, color: '#1a1a2e' }}>Kullanıcı Ata</div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>KULLANICI SEÇ</label>
            <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', marginBottom: 18, boxSizing: 'border-box' }}>
              <option value="">Kullanıcı seçin...</option>
              {allUsers.filter(u => !enrolledUsers.find(e => e.id === u.id || e.email === u.email)).map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.username})</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowEnrollModal(false)} style={{ padding: '9px 18px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white', cursor: 'pointer', fontWeight: 600 }}>İptal</button>
              <button onClick={enrollUser} style={{ padding: '9px 22px', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Ata</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => { confirmModal.action && confirmModal.action(); }}
        onCancel={closeConfirm}
        confirmText="Evet, Sil"
      />
      {/* Değerlendirmeler */}
      {ratings.length > 0 && (
        <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginTop: 20 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 }}>
              ⭐ Değerlendirmeler
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>
                {(ratings.reduce((a, r) => a + r.rating, 0) / ratings.length).toFixed(1)}
              </span>
              <div>
                <div style={{ color: '#f59e0b', fontSize: 14 }}>{'★'.repeat(Math.round(ratings.reduce((a, r) => a + r.rating, 0) / ratings.length))}{'☆'.repeat(5-Math.round(ratings.reduce((a, r) => a + r.rating, 0) / ratings.length))}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{ratings.length} değerlendirme</div>
              </div>
            </div>
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {ratings.map(r => (
              <div key={r.id} style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.04)', display: 'flex', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #E30613, #8b0000)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                  {r.user_name?.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{r.user_name}</div>
                    <div style={{ color: '#f59e0b', fontSize: 13 }}>{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div>
                  </div>
                  {r.comment && <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>{r.comment}</div>}
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{new Date(r.created_at).toLocaleDateString('tr-TR')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {completeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: '32px 28px', maxWidth: 440, width: '100%', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' }}>
            
            {completeSuccess && (
              <div style={{ background: 'rgba(52,199,89,0.1)', border: '1px solid rgba(52,199,89,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, color: '#34c759', fontWeight: 600, fontSize: 13 }}>
                ✅ {completeSuccess}
              </div>
            )}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 13, color: '#555' }}>Kullanıcı Seçin</label>
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                placeholder="İsim veya sicil no ile ara..."
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white' }}>
                {allUsers.filter(u => !userSearch || u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.username.includes(userSearch)).map(u => (
                  <div key={u.id} onClick={() => setSelectedUser(u.id)}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5', background: selectedUser === u.id ? 'rgba(227,6,19,0.06)' : 'transparent', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => { if (selectedUser !== u.id) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
                    onMouseLeave={e => { if (selectedUser !== u.id) e.currentTarget.style.background = 'transparent'; }}>
                    <div>
                      <div style={{ fontWeight: selectedUser === u.id ? 700 : 500, fontSize: 13, color: '#1a1a2e' }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{u.username} {u.department ? '· ' + u.department : ''}</div>
                    </div>
                    {selectedUser === u.id && <span style={{ color: '#E30613', fontSize: 16 }}>✓</span>}
                  </div>
                ))}
                {allUsers.filter(u => !userSearch || u.name.toLowerCase().includes(userSearch.toLowerCase()) || u.username.includes(userSearch)).length === 0 && (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Kullanıcı bulunamadı</div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setCompleteModal(false); setSelectedUser(''); setCompleteSuccess(''); }}
                style={{ flex: 1, padding: '12px', border: '1.5px solid #e5e7eb', borderRadius: 50, background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#6b7280' }}>
                İptal
              </button>
              <button onClick={completeUser} disabled={!selectedUser || completing}
                style={{ flex: 1, padding: '12px', border: 'none', borderRadius: 50, background: selectedUser ? '#34c759' : '#d1d5db', color: 'white', cursor: selectedUser ? 'pointer' : 'not-allowed', fontWeight: 700, fontSize: 14 }}>
                {completing ? 'İşleniyor...' : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <Play size={16} fill="white" /> Başlat
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
